/*
 * Hooks for id paging that keep track of visited pages. Enables moving to previous page for APIs that only return next page id.
 * */

import { useState } from "react";

export const useIdPaging = (firstPageId = "") => {
  const [visitedPageIds, setVisitedPageIds] = useState([firstPageId]);
  const currentPageId = visitedPageIds[visitedPageIds.length - 1];
  const prevPageId = visitedPageIds[visitedPageIds.length - 2];

  const moveToPage = (pageId: string) => {
    const pageIdIndex = visitedPageIds.indexOf(pageId);
    setVisitedPageIds(visitedPageIds.slice(0, pageIdIndex + 1));
  };

  const addLastPageId = (lastPageId: string) =>
    setVisitedPageIds([...visitedPageIds, lastPageId]);

  const onChangePage = (newPageId: string) =>
    visitedPageIds.includes(newPageId)
      ? moveToPage(newPageId)
      : addLastPageId(newPageId);

  return {
    onChangePage,
    currentPageId,
    prevPageId
  };
};

// Suited for components that implement paging using HOC withPagination.
const useIdPagination = (firstPageId = "", initialPageSize = 10) => {
  const { currentPageId, prevPageId, onChangePage } = useIdPaging(firstPageId);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const onPaginationChange = (
    newPageId: string,
    newPageSize?: number
  ): void => {
    if (newPageSize) {
      setPageSize(newPageSize);
    }
    onChangePage(newPageId);
  };

  return {
    onPaginationChange,
    currentPageId,
    prevPageId,
    pageSize
  };
};

export default useIdPagination;
